import qs from 'qs';
import { Paths, createPath } from '../../../app/router/app.router';
import { SettingsTreeDataRequestType } from '../reducer/settings-tree-reducer.types';

/**
 * A reusable contract for deep-linking into the Settings tree and pre-selecting a node.
 *
 * Any page can build one of these links (see {@link buildSettingsDeepLink}); the Settings page reads
 * it on mount and keeps it in sync as the selection changes, so the URL is always a shareable
 * snapshot of the current selection.
 */
export interface SettingsDeepLinkTarget {
  /** Entity id of the node to select (e.g. a hub id). */
  entityId: string;
  /**
   * Disambiguator for nodes that appear multiple times in the tree. The selected occurrence is the
   * one whose `request.rootId === scope` (e.g. a hub's managing project id). This is deliberately a
   * free param and is NOT assumed to equal the path `projectID`, since a node can live under a
   * sub-project whose root differs from the page root.
   */
  scope?: string;
  /** Which tree slice the node belongs to. Defaults to `ProjectChildren` (most targets are descendants). */
  nodeType?: SettingsTreeDataRequestType;
  /** Human-readable name, used only for the "couldn't find it" fallback toast. */
  label?: string;
}

// The raw query-param keys that make up the contract. Kept short and stable so links remain readable.
interface SettingsDeepLinkParams {
  node?: string;
  scope?: string;
  nodeType?: string;
  label?: string;
}

/** Builds just the `?...` query portion of a deep-link, used when rewriting params without the path. */
export function buildSettingsDeepLinkSearch(target: SettingsDeepLinkTarget): string {
  const params: SettingsDeepLinkParams = {
    node: target.entityId,
    scope: target.scope,
    nodeType: target.nodeType,
    label: target.label
  };
  return qs.stringify(params, { addQueryPrefix: true, skipNulls: true });
}

/** Builds a full settings deep-link path + query for the given root project. */
export function buildSettingsDeepLink(projectID: string, target: SettingsDeepLinkTarget): string {
  return `${createPath(Paths.settings, { projectID })}${buildSettingsDeepLinkSearch(target)}`;
}

/** Parses a `location.search` string into a target, or `undefined` if it carries no node reference. */
export function parseSettingsDeepLink(search: string): SettingsDeepLinkTarget | undefined {
  const params = qs.parse(search, { ignoreQueryPrefix: true }) as SettingsDeepLinkParams;
  if (!params.node) {
    return undefined;
  }
  return {
    entityId: params.node,
    scope: params.scope || undefined,
    nodeType:
      params.nodeType === SettingsTreeDataRequestType.Projects
        ? SettingsTreeDataRequestType.Projects
        : params.nodeType === SettingsTreeDataRequestType.ProjectChildren
        ? SettingsTreeDataRequestType.ProjectChildren
        : undefined,
    label: params.label || undefined
  };
}
